/**
 * Reusable validation utilities for course and course request forms
 */

export const ROOM_NUMBER_MAX_LENGTH = 100;
export const WEB_URL_MAX_LENGTH = 500;

export interface CourseValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface CourseRequestData {
  name?: string;
  creditHours?: string | number;
  courseType?: string;
  description?: string;
  location?: string;
  courseDate?: string | Date;
  startTime?: string;
  endTime?: string;
  async?: boolean;
  anytime?: boolean;
  virtual?: boolean;
  roomNumber?: string;
  webUrl?: string;
}

// KalpaBoolean can be 0, 1, or boolean
type KalpaBoolean = 0 | 1 | boolean;

export interface CourseData {
  courseName?: string;
  trackingValue?: string | number;
  courseType?: any;
  courseDescription?: string;
  location?: string;
  courseSize?: number;
  dateOffered?: string | Date;
  sessionStartDate?: string | Date;
  sessionEndDate?: string | Date;
  regStartDate?: string | Date;
  regEndDate?: string | Date;
  isAsync?: KalpaBoolean;
  isAnytime?: KalpaBoolean;
  virtualFlag?: KalpaBoolean;
  roomNumber?: string;
  hyperlink?: string;
}

export function courseDateOfferedIsPresent(course: CourseData | any): boolean {
  const d = course?.dateOffered;
  if (d == null) return false;
  if (typeof d === 'string' && d.trim() === '') return false;
  if (d instanceof Date) return !isNaN(d.getTime());
  return true;
}

// Helper function to convert KalpaBoolean to boolean
function toBoolean(value: KalpaBoolean | undefined): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  return value === 1;
}

/**
 * Validates course request general info step
 * @param course - The course request data object
 * @returns Validation result with isValid flag and array of error messages
 */
export function validateCourseRequestGeneralInfo(course: CourseRequestData): CourseValidationResult {
  const errors: string[] = [];

  // Required fields
  if (!course.name || course.name.trim() === '') {
    errors.push('Course Name is required');
  }

  if (!course.creditHours || course.creditHours === '' || Number(course.creditHours) <= 0) {
    errors.push('Credit Hours is required and must be greater than 0');
  }

  if (!course.courseType || course.courseType.trim() === '') {
    errors.push('Course Type is required');
  }

  if (!course.location || course.location.trim() === '') {
    errors.push('Location is required');
  }

  // Date and time validation based on async/anytime flags
  if (!course.async) {
    if (!course.courseDate) {
      errors.push('Course Date is required');
    }

    if (!course.anytime) {
      if (!course.startTime || course.startTime.trim() === '') {
        errors.push('Course Start Time is required');
      }

      if (!course.endTime || course.endTime.trim() === '') {
        errors.push('Course End Time is required');
      }
    }
  }

  // Length validation (only check roomNumber when the field is visible)
  if (!course.virtual && course.roomNumber && course.roomNumber.trim().length > ROOM_NUMBER_MAX_LENGTH) {
    errors.push(`Room Number must be ${ROOM_NUMBER_MAX_LENGTH} characters or less`);
  }

  if (course.webUrl && course.webUrl.trim().length > WEB_URL_MAX_LENGTH) {
    errors.push(`Web URL must be ${WEB_URL_MAX_LENGTH} characters or less`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates course general info step (for add/edit course)
 * @param course - The course data object (can be Course class or CourseData)
 * @returns Validation result with isValid flag and array of error messages
 */
export function validateCourseGeneralInfo(course: CourseData | any): CourseValidationResult {
  const errors: string[] = [];

  // Required fields
  if (!course.courseName || (typeof course.courseName === 'string' && course.courseName.trim() === '')) {
    errors.push('Course Name is required');
  }

  if (!course.trackingValue || course.trackingValue === '' || Number(course.trackingValue) <= 0) {
    errors.push('Hours is required and must be greater than 0');
  }

  // Course Type validation - check if it's a valid object with courseType property
  if (!course.courseType || 
      (typeof course.courseType === 'object' && (!course.courseType.courseType || course.courseType.courseType.trim() === '')) ||
      (typeof course.courseType === 'string' && course.courseType.trim() === '')) {
    errors.push('Course Type is required');
  }

  if (!course.courseSize || course.courseSize <= 0) {
    errors.push('Course Size is required and must be greater than 0');
  }

  if (course.hyperlink && typeof course.hyperlink === 'string' && course.hyperlink.trim().length > WEB_URL_MAX_LENGTH) {
    errors.push(`Web URL must be ${WEB_URL_MAX_LENGTH} characters or less`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates course details step (for add/edit course)
 * @param course - The course data object (can be Course class or CourseData)
 * @param requireCatalogDates - Whether to require catalog availability dates (default true; pass false for course-request approvals where the field is hidden)
 * @returns Validation result with isValid flag and array of error messages
 */
export function validateCourseDetails(course: CourseData | any, requireCatalogDates = true): CourseValidationResult {
  const errors: string[] = [];

  if (!course.location || course.location.trim() === '') {
    errors.push('Location is required');
  }

  // Convert KalpaBoolean to boolean for validation
  const isAnytime = toBoolean(course.isAnytime);
  const isAsync = toBoolean(course.isAsync);
  const isVirtual = toBoolean(course.virtualFlag);

  // Room Number length (only when field is visible — non-virtual courses)
  if (!isVirtual && course.roomNumber && typeof course.roomNumber === 'string' && course.roomNumber.trim().length > ROOM_NUMBER_MAX_LENGTH) {
    errors.push(`Room Number must be ${ROOM_NUMBER_MAX_LENGTH} characters or less`);
  }

  // Date and time validation based on async/anytime flags
  if (isAnytime) {
    if (!courseDateOfferedIsPresent(course)) {
      errors.push('Course Date is required for Anytime courses');
    }
  } else if (!isAsync) {
    if (!courseDateOfferedIsPresent(course)) {
      errors.push('Course Date is required');
    }

    if (!course.sessionStartDate) {
      errors.push('Course Start Time is required');
    }

    if (!course.sessionEndDate) {
      errors.push('Course End Time is required');
    }
  }

  if (requireCatalogDates) {
    const isValidCatalogDate = (d: Date | string | undefined): boolean => {
      if (!d) return false;
      const date = d instanceof Date ? d : new Date(d as string);
      return !isNaN(date.getTime()) && date.getFullYear() >= 1000;
    };
    if (!course.regStartDate) {
      errors.push('Catalog availability start date is required');
    } else if (!isValidCatalogDate(course.regStartDate)) {
      errors.push('Catalog availability start date must include a 4-digit year');
    }
    if (!course.regEndDate) {
      errors.push('Catalog availability end date is required');
    } else if (!isValidCatalogDate(course.regEndDate)) {
      errors.push('Catalog availability end date must include a 4-digit year');
    }
    if (isValidCatalogDate(course.regStartDate) && isValidCatalogDate(course.regEndDate)) {
      const startD = course.regStartDate instanceof Date ? course.regStartDate : new Date(course.regStartDate as string);
      const endD = course.regEndDate instanceof Date ? course.regEndDate : new Date(course.regEndDate as string);
      if (endD < startD) {
        errors.push('Catalog availability end date must be after start date');
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export interface ManagedCourseApprovalValidationOptions {
  requireCatalogAvailabilityDates: boolean;
  requireAtLeastOneRequirement: boolean;
  stateRequirement?: { isRequired: boolean; isValid: boolean };
}

export function validateManagedCourseApprovalForm(
  course: CourseData | any,
  options: ManagedCourseApprovalValidationOptions,
): CourseValidationResult {
  const merged: string[] = [];
  merged.push(...validateCourseGeneralInfo(course).errors);
  merged.push(...validateCourseDetails(course).errors);

  if (options.requireCatalogAvailabilityDates) {
    if (!course.regStartDate) merged.push('Catalog availability start date is required');
    if (!course.regEndDate) merged.push('Catalog availability end date is required');
  }

  if (options.requireAtLeastOneRequirement) {
    const list = course.requirementsList ?? [];
    if (!Array.isArray(list) || list.length === 0) {
      merged.push('At least one requirement must be selected');
    }
  }

  if (
    options.stateRequirement?.isRequired === true &&
    options.stateRequirement.isValid !== true
  ) {
    merged.push('State reporting requirement is required');
  }

  const errors = [...new Set(merged)];
  return { isValid: errors.length === 0, errors };
}

